<template>
  <div class="report-dashboard-container">
    <el-card class="page-header-card" shadow="hover">
      <div class="page-header">
        <div class="page-title-section">
          <h2 class="page-title">
            <font-awesome-icon icon="chart-line" class="page-title-icon" />
            Тайлангийн Dashboard
          </h2>
          <p class="page-subtitle">Тайлангийн ерөнхий мэдээлэл, статистик</p>
        </div>
        <el-button type="primary" @click="refreshAll" size="large" class="refresh-button">
          <font-awesome-icon icon="arrows-rotate" class="button-icon" />
          Шинэчлэх
        </el-button>
      </div>
    </el-card>

    <!-- KPI Cards -->
    <el-row :gutter="16" class="kpi-cards-row mt-10">
      <el-col :xs="24" :sm="12" :md="6" :lg="6" :xl="6" v-for="(kpi, index) in kpis" :key="index">
        <el-card class="kpi-card" :class="`kpi-card-${index % 4}`" shadow="hover">
          <div class="kpi-content">
            <div class="kpi-icon-wrapper">
              <font-awesome-icon :icon="getKpiIcon(index)" class="kpi-icon" />
            </div>
            <div class="kpi-info">
              <div class="kpi-label">{{ kpi.label }}</div>
              <div class="kpi-value">{{ kpi.value }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Тайлангууд -->
    <el-card class="reports-card mt-10" shadow="hover">
      <template #header>
        <div class="table-header">
          <font-awesome-icon icon="chart-pie" class="table-header-icon" />
          <span class="table-header-title">Тайлангууд</span>
          <el-tag type="info" class="table-count-tag">{{ reports.length }} тайлан</el-tag>
        </div>
      </template>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12" v-for="report in reports" :key="report.id">
          <el-card class="report-card" shadow="hover">
            <div class="report-card-header">
              <div class="report-title-section">
                <h3 class="report-title">{{ report.name }}</h3>
                <el-tag :type="report.is_active ? 'success' : 'info'" size="small" class="report-status-tag">
                  {{ report.is_active ? 'Идэвхтэй' : 'Идэвхгүй' }}
                </el-tag>
              </div>
              <el-button type="primary" size="small" @click="viewReport(report.id)" class="view-button">
                <font-awesome-icon icon="eye" class="button-icon-small" />
                Харах
              </el-button>
            </div>
            <div class="report-stats">
              <div class="stat-item">
                <font-awesome-icon icon="clock-rotate-left" class="stat-icon" />
                <span class="stat-label">Сүүлийн ажиллуулалт:</span>
                <span class="stat-value">{{ getLastExecution(report.id) }}</span>
              </div>
              <div class="stat-item">
                <font-awesome-icon icon="chart-line" class="stat-icon" />
                <span class="stat-label">Нийт ажиллуулалт:</span>
                <span class="stat-value">{{ getExecutionCount(report.id) }}</span>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import services from '@/services'
import { ElMessage } from 'element-plus'

export default {
  name: 'ReportDashboard',
  data () {
    return {
      reports: [],
      analytics: {},
      kpis: []
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    async loadData () {
      try {
        // Dashboard дээр бүх тайланг (идэвхтэй болон идэвхгүй) харуулах
        const [reportsRes] = await Promise.all([
          services.get('reports', { include_inactive: 'true' })
        ])
        if (reportsRes && reportsRes.type === 'success') {
          this.reports = reportsRes.data || []
          // Тайлан бүрийн статистик авах
          await this.loadReportStats()
        }
        this.calculateKPIs()
      } catch (error) {
        ElMessage.error('Өгөгдөл ачаалахад алдаа гарлаа')
      }
    },
    async loadReportStats () {
      // Тайлан бүрийн статистик авах
      for (const report of this.reports) {
        try {
          const statsRes = await services.get(`reports/${report.id}/stats`)
          if (statsRes && statsRes.type === 'success') {
            report.stats = statsRes.data
          }
        } catch (error) {
          console.error(`Error loading stats for report ${report.id}:`, error)
        }
      }
    },
    calculateKPIs () {
      // Өнөөдрийн нийт ажиллуулалт
      const todayCount = this.reports.reduce((sum, r) => {
        return sum + (r.stats?.today_count || 0)
      }, 0)

      // Энэ сарын нийт ажиллуулалт
      const monthCount = this.reports.reduce((sum, r) => {
        return sum + (r.stats?.month_count || 0)
      }, 0)

      this.kpis = [
        { label: 'Нийт тайлан', value: this.reports.length },
        { label: 'Идэвхтэй тайлан', value: this.reports.filter(r => r.is_active).length },
        { label: 'Өнөөдрийн ажиллуулалт', value: todayCount },
        { label: 'Энэ сарын ажиллуулалт', value: monthCount }
      ]
    },
    getLastExecution (reportId) {
      const report = this.reports.find(r => r.id === reportId)
      if (report && report.stats && report.stats.last_execution) {
        const date = new Date(report.stats.last_execution)
        return date.toLocaleDateString('mn-MN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      return 'Одоогоор байхгүй'
    },
    getExecutionCount (reportId) {
      const report = this.reports.find(r => r.id === reportId)
      if (report && report.stats && report.stats.total_executions) {
        return report.stats.total_executions
      }
      return 0
    },
    getKpiIcon (index) {
      const icons = ['chart-pie', 'check-circle', 'calendar-day', 'calendar-month']
      return icons[index] || 'chart-line'
    },
    viewReport (id) {
      this.$router.push(`/reports/${id}`)
    },
    refreshAll () {
      this.loadData()
      ElMessage.success('Шинэчлэгдлээ')
    }
  }
}
</script>

<style lang="scss" scoped>
/* Report Dashboard Container */
.report-dashboard-container {
  padding: 0;
}

/* Page Header Card */
.page-header-card {
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: none;
  margin-bottom: 24px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title-section {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title-icon {
  color: #667eea;
  font-size: 32px;
}

.page-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.refresh-button {
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.refresh-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.button-icon {
  margin-right: 8px;
}

/* KPI Cards */
.kpi-cards-row {
  margin: 0 -8px;
}

.kpi-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: none;
  transition: all 0.3s ease;
  margin-bottom: 16px;
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.kpi-card-0 {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.kpi-card-1 {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.kpi-card-2 {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.kpi-card-3 {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.kpi-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px;
}

.kpi-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.kpi-icon {
  color: white;
}

.kpi-info {
  flex: 1;
}

.kpi-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
  font-weight: 500;
}

.kpi-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
}

/* Reports Card */
.reports-card {
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: none;
}

.table-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-header-icon {
  color: #667eea;
  font-size: 20px;
}

.table-header-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
}

.table-count-tag {
  border-radius: 20px;
  padding: 4px 12px;
}

/* Report Cards */
.report-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: none;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.report-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.report-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.report-title-section {
  flex: 1;
}

.report-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.report-status-tag {
  border-radius: 12px;
}

.view-button {
  border-radius: 6px;
  transition: all 0.2s ease;
}

.view-button:hover {
  transform: translateY(-1px);
}

.button-icon-small {
  margin-right: 4px;
  font-size: 12px;
}

.report-stats {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.stat-icon {
  color: #667eea;
  font-size: 16px;
}

.stat-label {
  color: #6b7280;
  font-weight: 500;
}

.stat-value {
  color: #1f2937;
  font-weight: 600;
  margin-left: auto;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-title {
    font-size: 24px;
  }

  .refresh-button {
    width: 100%;
    margin-top: 8px;
  }

  .kpi-content {
    flex-direction: column;
    text-align: center;
  }

  .kpi-icon-wrapper {
    margin: 0 auto;
  }

  .report-card-header {
    flex-direction: column;
    gap: 12px;
  }

  .view-button {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
  }

  .kpi-value {
    font-size: 24px;
  }

  .report-title {
    font-size: 16px;
  }
}
</style>
